"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";
import { WorkingLabel } from "@/components/counter/WorkingLabel";
import { cn } from "@/lib/utils";

/**
 * Modal and drawer for the counter.
 *
 * These exist because their absence is why every pack screen ended up read-only: with no overlay
 * primitive to reach for, screens rendered static markup instead of "tap → edit → save".
 *
 * Counter-shaped, not web-shaped: large hit areas, Escape always closes, focus is trapped so a
 * cashier tabbing through a form cannot fall out into the board behind it, and the backdrop is
 * ink at low alpha rather than black so the paper world stays visible underneath.
 */

function useDismiss(open: boolean, onClose: () => void, ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !ref.current) return;
      const focusable = ref.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    // Move focus in so the first field is ready to type into.
    const t = setTimeout(() => {
      ref.current?.querySelector<HTMLElement>("input,select,textarea,button")?.focus();
    }, 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose, ref]);
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = "max-w-lg",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const tc = useTranslations("common");
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, onClose, ref);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label={tc("close")}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-bg-overlay"
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={cn(
          "relative flex w-full flex-col rounded-tile border border-stroke-strong bg-bg-base shadow-overlay",
          width,
        )}
      >
        <header className="border-b border-stroke-weak px-5 py-3.5">
          <h2 id="modal-title" className="font-text text-lg font-bold text-text-strong">
            {title}
          </h2>
          {description && <p className="mt-0.5 text-sm text-text-weak">{description}</p>}
        </header>
        <div className="max-h-[70dvh] overflow-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-stroke-weak px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/** Right-hand drawer — for detail that belongs beside the board, not on top of it. */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width = "w-[420px]",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const tc = useTranslations("common");
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, onClose, ref);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label={tc("close")}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-bg-overlay"
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        className={cn(
          "relative flex h-full flex-col border-l border-stroke-strong bg-bg-base shadow-overlay",
          width,
        )}
      >
        <header className="flex items-center justify-between border-b border-stroke-weak px-5 py-3.5">
          <h2 id="drawer-title" className="font-text text-lg font-bold text-text-strong">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={tc("close")}
            className="grid min-h-target-dense min-w-target-dense place-items-center rounded-control text-text-weak transition-colors duration-150 ease-counter hover:bg-bg-base hover:text-text-strong"
          >
            ✕
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-stroke-weak px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/**
 * Destructive-action confirm. Voiding a fired item or discarding a bill is not undoable at a
 * counter, so it gets an explicit stop rather than an optimistic toast.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  destructive = true,
  busy = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: string;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
}) {
  const tc = useTranslations("common");
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width="max-w-md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="min-h-target-dense rounded-control border border-stroke-strong px-3.5 py-2 text-base font-semibold text-text-weak transition-colors duration-150 ease-counter hover:border-stroke-strong hover:text-text-strong"
          >
            {tc("cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={cn(
              "min-h-target rounded-control px-3.5 py-2 text-base font-semibold text-text-on-fill transition-colors duration-150 ease-counter disabled:opacity-60",
              destructive ? "bg-fill-error hover:bg-alert-700" : "bg-fill-brand hover:bg-fill-brand-hover",
            )}
          >
            {busy ? <WorkingLabel /> : (confirmLabel ?? tc("confirm"))}
          </button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-text-weak">{body}</p>
    </Modal>
  );
}

/**
 * A confirm that must capture WHY.
 *
 * Rejecting a sample, overriding a custody step, voiding a fired item, closing a drawer that is
 * short — none of these are answerable with yes/no, because the reason is the record. `ConfirmDialog`
 * takes only a body string, so three verticals independently hand-rolled this on top of `Modal`.
 * It belongs in the kit once.
 *
 * The reason is required by default and the confirm stays disabled until it is given, so the
 * dialog cannot produce an unattributed decision.
 */
export function ReasonDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  label = "Reason",
  hint,
  placeholder,
  presets,
  confirmLabel = "Confirm",
  destructive = true,
  busy = false,
  minLength = 4,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  title: string;
  body?: string;
  label?: string;
  hint?: string;
  placeholder?: string;
  /** Common answers, one tap. The operator can still type their own. */
  presets?: string[];
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  minLength?: number;
}) {
  const [reason, setReason] = useState("");
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setReason("");
  }

  const tooShort = reason.trim().length < minLength;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={body}
      width="max-w-md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="min-h-target-dense rounded-control border border-stroke-strong px-3.5 py-2 text-base font-semibold text-text-weak transition-colors duration-150 ease-counter hover:border-stroke-strong hover:text-text-strong"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || tooShort}
            className={cn(
              "min-h-target rounded-control px-3.5 py-2 text-base font-semibold text-text-on-fill transition-colors duration-150 ease-counter disabled:cursor-not-allowed disabled:opacity-50",
              destructive ? "bg-fill-error hover:bg-alert-700" : "bg-fill-brand hover:bg-fill-brand-hover",
            )}
          >
            {busy ? <WorkingLabel /> : confirmLabel}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {presets && presets.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={reason === p}
                onClick={() => setReason(p)}
                className={cn(
                  "min-h-target-dense rounded-control border px-3 text-base font-semibold transition-colors duration-150 ease-counter",
                  reason === p
                    ? "border-stroke-weak bg-fill-accent-weak text-honey-800"
                    : "border-stroke-weak bg-bg-base text-text-weak hover:border-stroke-strong hover:text-text-strong",
                )}
              >
                {p}
              </button>
            ))}
          </div>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold uppercase tracking-[0.07em] text-text-disabled">
            {label}
            <span className="ml-1 text-text-error">*</span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
            aria-label={label}
            className="min-h-[76px] w-full resize-y rounded-control border border-stroke-strong bg-bg-base px-3 py-2 text-base leading-relaxed text-text-strong outline-none placeholder:text-text-disabled focus:border-stroke-brand"
          />
          <span className="text-sm text-text-disabled">
            {hint ?? "Filed with the record — it is what someone reads when they ask why."}
          </span>
        </label>
      </div>
    </Modal>
  );
}
